/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface StateProps {
  id: string;
  name: string;
  code: string;
  isLicenseRequiredToFly: boolean | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class StateRecord extends Record<StateProps>({
  id: null as any,
  name: null as any,
  code: null as any,
  isLicenseRequiredToFly: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    code: string;
    isLicenseRequiredToFly: boolean | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): StateRecord {
    if (json == null) {
      throw new Error('StateRecord.fromJS: parameter cannot be null');
    }
    return new StateRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'StateJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'StateJson.name'),
      code: getFromOrThrow(json, (j) => j.code, 'StateJson.code'),
      isLicenseRequiredToFly: json.isLicenseRequiredToFly,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    code: string;
    isLicenseRequiredToFly: boolean | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [StateRecord, Error[]] {
    if (json == null) {
      throw new Error('StateRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new StateRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'StateJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'StateJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      code: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.code, 'StateJson.code'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isLicenseRequiredToFly: tryOrDefault(
        () => json.isLicenseRequiredToFly,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: StateProps) {
    super(props);
    if (props == null) {
      throw new Error('StateRecord constructor parameter cannot be null');
    }
  }
}

export abstract class StateViewModelBase implements StateProps {
  protected readonly _props: StateProps;
  private _json?: StateProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get code(): string {
    return this._props.code;
  }
  get isLicenseRequiredToFly(): boolean | null {
    return this._props.isLicenseRequiredToFly;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: StateProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        code: this._props.code,
        isLicenseRequiredToFly: this._props.isLicenseRequiredToFly,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllStateEntities = (state: AppState) => state.entityStores.state.entities;

export const selectStateById = (id: string) => (state: AppState) =>
  state.entityStores.state.entities.get(id);

export const selectStateMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.state.meta.get(id);

export const selectStateAndMetadataById = (id: string) =>
  createSelector(selectStateById(id), selectStateMetadataById(id), (state, meta) => ({
    state,
    meta,
  }));

export function selectStateEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllStateEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, StateRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectStateEntitiesByName(name: string) {
  return createSelector(selectAllStateEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectStateEntitiesByCode(code: string) {
  return createSelector(selectAllStateEntities, (entities) =>
    entities.filter((e) => e.code === code).valueSeq(),
  );
}

export function selectStateEntitiesByIsLicenseRequiredToFly(isLicenseRequiredToFly: boolean) {
  return createSelector(selectAllStateEntities, (entities) =>
    entities.filter((e) => e.isLicenseRequiredToFly === isLicenseRequiredToFly).valueSeq(),
  );
}
//#endregion Selectors
